import { CalculationService } from '@/services/calculation.service.ts'
import { EventService } from '@/services/event.service.ts'
import { useMappingStore } from '@/stores/mappingStore.ts'
import { useCalculationStore } from '@/stores/calculationStore.ts'
import { useTracking } from '@/composables/tracking'
import { nextTick } from 'vue'
import { useTrackingInteractionStore } from '@/stores/trackingInteractionStore'
import { storeToRefs } from 'pinia'

export type ValueRule = {
  type: string
  arg: any
  field: string
  value: any
}

export interface ValueRuleService {
  rule: (id: string) => object

  getRules(ruleList: Array<ValueRule>): Function[]
}

export const ValueRuleService: ValueRuleService = {
  rule: (id: string) => rules[id as keyof Object],
  getRules(ruleList: Array<ValueRule>): Function[] {
    return ruleList?.map((rule) => rules(rule)) ?? []
  }
}

const rules = (config: ValueRule) => {
  const eventService = EventService()
  const trackingInteractionStore = useTrackingInteractionStore()
  const { miniInteractionCount } = storeToRefs(trackingInteractionStore)
  const rules: { [key: string]: Function } = {
    update_calculation: async () => {
      await CalculationService.updateCalculations(config.arg)
    },
    disable_field_on_value: async (fieldValue: any) => {
      const value = fieldValue === config.value
      eventService.setDisabled(config.field, value)
    },
    set_value_on_value: async (fieldValue: any) => {
      if (fieldValue === config.value) eventService.setFieldValue(config.field, config.arg)
    },
    set_config_default_value: async (fieldValue: any) => {
      // If a value matcher is provided on the rule, only apply when it matches
      if (typeof config.value !== 'undefined' && fieldValue !== config.value) return

      const mappingStore = useMappingStore()
      const calculationStore = useCalculationStore()

      const configFieldName = config.arg
      const configField = calculationStore.config?.fields.find((field) => field.name === configFieldName)
      if (configField && typeof configField.default !== 'undefined') {
        mappingStore.getFieldsOfKey('config', configFieldName).value.forEach((field) => {
          field.config.value = configField.default
        })
      }
    },
    mini_track_value_change: async () => {
      try {
        await nextTick()
        // Only track the first value change to avoid excessive event firing while users are inputting values
        if (miniInteractionCount.value < 1) useTracking().changeValueMini()
        trackingInteractionStore.incrementMiniInteractionCount()
      } catch (error) {
        console.error('Error while handling tracking', error)
      }
    }
  }
  return rules[config.type]
}
